import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Stato di pagina persistito nella query string.
 *
 * Serve a far sì che il tasto "indietro" del browser e il refresh riportino
 * l'utente esattamente dove era: tab aperto, cliente selezionato, filtri.
 * Tutto ciò che passa da qui finisce nell'URL, quindi sopravvive al reload ed è
 * anche condivisibile via link.
 *
 * - i valori uguali al default non vengono scritti nell'URL (URL puliti)
 * - le chiavi elencate in `push` creano una voce di cronologia (il tasto
 *   indietro le annulla una alla volta) e vengono scritte subito
 * - tutte le altre usano `replace` con un piccolo ritardo: digitare in un campo
 *   di ricerca non riempie la cronologia e non supera il limite di chiamate a
 *   history.replaceState di Safari
 */
export type HistoryMode = 'push' | 'replace';

// Ritardo di scrittura nell'URL per gli aggiornamenti "replace" (digitazione).
const WRITE_DELAY_MS = 350;

export function usePageState<K extends string>(
  defaults: Record<K, string>,
  options: { push?: readonly NoInfer<K>[] } = {}
): [Record<K, string>, (patch: Partial<Record<K, string | null>>, mode?: HistoryMode) => void] {
  const location = useLocation();
  const navigate = useNavigate();

  // defaults e push arrivano come letterali inline: li congeliamo al primo
  // render per non invalidare memo/callback a ogni giro.
  const defaultsRef = useRef(defaults);
  const pushKeysRef = useRef(options.push ?? []);

  const fromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const out = {} as Record<K, string>;
    (Object.keys(defaultsRef.current) as K[]).forEach((key) => {
      const value = params.get(key);
      out[key] = value === null ? defaultsRef.current[key] : value;
    });
    return out;
  }, [location.search]);

  // Valori già scelti dall'utente ma non ancora scritti nell'URL (finestra di
  // debounce): l'interfaccia deve mostrarli subito.
  const overridesRef = useRef<Partial<Record<K, string>>>({});
  const [renderTick, forceRender] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Query "in volo": più chiamate nello stesso handler devono sommarsi, non
  // sovrascriversi (navigate applica il cambio in modo asincrono).
  const pendingRef = useRef<string | null>(null);

  useEffect(() => {
    pendingRef.current = null;
    // Un override è servito finché l'URL non lo rispecchia; quelli più recenti
    // della scrittura appena avvenuta restano in attesa del prossimo flush.
    const params = new URLSearchParams(location.search);
    (Object.keys(overridesRef.current) as K[]).forEach((key) => {
      const urlValue = params.get(key) ?? defaultsRef.current[key];
      if (urlValue === overridesRef.current[key]) delete overridesRef.current[key];
    });
  }, [location.search]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const state = useMemo(
    () => ({ ...fromUrl, ...overridesRef.current }),
    // overridesRef è una ref: a farlo rileggere è renderTick
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fromUrl, renderTick]
  );

  const write = useCallback(
    (patch: Partial<Record<K, string | null>>, push: boolean) => {
      // Leggiamo da window.location e non dalla location del render: navigate()
      // aggiorna la cronologia in modo sincrono, quindi una scrittura ritardata
      // parte sempre dall'URL davvero corrente.
      const current = pendingRef.current ?? new URLSearchParams(window.location.search).toString();
      const params = new URLSearchParams(current);

      const applica = (key: K, value: string | null | undefined) => {
        if (value === null || value === undefined || value === '' || value === defaultsRef.current[key]) {
          params.delete(key);
        } else {
          params.set(key, value);
        }
      };

      (Object.keys(overridesRef.current) as K[]).forEach((key) => applica(key, overridesRef.current[key]));
      (Object.keys(patch) as K[]).forEach((key) => applica(key, patch[key]));

      const next = params.toString();
      if (next === current) return;

      pendingRef.current = next;
      navigate({ pathname: window.location.pathname, search: next ? `?${next}` : '' }, { replace: !push });
    },
    [navigate]
  );

  const setState = useCallback(
    (patch: Partial<Record<K, string | null>>, mode?: HistoryMode) => {
      const shouldPush = mode
        ? mode === 'push'
        : (Object.keys(patch) as K[]).some((key) => pushKeysRef.current.includes(key));

      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      if (shouldPush) {
        // Un cambio di pagina/tab si scrive subito: è una vera navigazione.
        write(patch, true);
        return;
      }

      (Object.keys(patch) as K[]).forEach((key) => {
        const value = patch[key];
        overridesRef.current[key] = value === null || value === undefined ? defaultsRef.current[key] : value;
      });
      forceRender((n) => n + 1);

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        write({}, false);
      }, WRITE_DELAY_MS);
    },
    [write]
  );

  return [state, setState];
}

export default usePageState;
